/**
 * @copyright
 * Copyright(c) 2020 SICK AG
 */
import { ValueRangeScale } from './ValueRange.js';
/**
 * A utility class to compute value ranges
 */

export class ValueRangeTools {
  /**
   * Ensures that the start and end value of the given value range are valid for a logarithmic scale.
   *
   * @param valueRange - the value range to be fixed
   */
  static _fixLogarithmicValueRange(valueRange) {
    if (valueRange === undefined) {
      return;
    }

    if (valueRange.scale !== ValueRangeScale.log10) {
      // do not fix a linear value range
      return;
    }

    if (typeof valueRange.start === 'number' && valueRange.start <= 0.0) {
      // eslint-disable-next-line
      console.error(`Start of a logarithmic value range must be greater than 0 but was ${valueRange.start}! The start will be computed automatically.`);
      delete valueRange.start;
    }

    if (typeof valueRange.end === 'number' && valueRange.end <= 0.0) {
      // eslint-disable-next-line
      console.error(`End of a logarithmic value range must be greater than 0 but was ${valueRange.end}! The end will be computed automatically.`);
      delete valueRange.end;
    }
  }
  /**
   * Computes the value-range scale from the given value range
   *
   * @param valueRange - the value range to take the scale from
   */


  static _computeValueRangeScale(valueRange) {
    if (valueRange === undefined || valueRange.scale === null || valueRange.scale === undefined) {
      // use a linear scale as default
      return ValueRangeScale.linear;
    }

    return valueRange.scale;
  }
  /**
   * Computes the value range.
   *
   * @param values - the values to compute the value range for
   * @param userDefinedValueRange - a value range defined by the user; properties of this parameter will supersede computed values
   * @returns a value range with all its properties guaranteed to be defined and non-null.
   */


  static COMPUTE_VALUE_RANGE(values, userDefinedValueRange) {
    this._fixLogarithmicValueRange(userDefinedValueRange);

    const scale = this._computeValueRangeScale(userDefinedValueRange);

    let minValue;
    let maxValue;

    if (userDefinedValueRange !== undefined) {
      // The user defined a value range -> check if we can use it
      minValue = userDefinedValueRange.start;
      maxValue = userDefinedValueRange.end;

      if (minValue !== undefined && maxValue !== undefined) {
        // everything is already defined by the user
        return {
          start: minValue,
          end: maxValue,
          scale: scale
        };
      }
    }

    let computedMinValue = Number.MAX_VALUE;
    let computedMaxValue = -Number.MAX_VALUE;
    let size = 0;

    for (const value of values) {
      if (scale !== ValueRangeScale.log10 || value > 0) {
        computedMinValue = Math.min(computedMinValue, value);
        computedMaxValue = Math.max(computedMaxValue, value);
        ++size;
      }
    }

    if (size === 0) {
      // no values available to compute the value range
      switch (scale) {
        case ValueRangeScale.linear:
          computedMinValue = 0.0;
          computedMaxValue = 100.0;
          break;

        case ValueRangeScale.log10:
          computedMinValue = 0.1;
          computedMaxValue = 1.0;
          break;

        default:
          throw new Error(`unsupported scale <${scale}>`);
      }
    } // eslint-disable-next-line


    console.assert(computedMinValue < computedMaxValue, `computed minimum is greater than or equal to computed maximum: ${computedMinValue} >= ${computedMaxValue}`);

    if (scale === ValueRangeScale.linear) {
      if (computedMinValue > 0) {
        // there are only positive values -> start from 0
        computedMinValue = 0;
      }

      if (computedMaxValue < 0) {
        // there are only negative values -> end with 0
        computedMaxValue = 0;
      }

      if (computedMinValue === computedMaxValue) {
        // special case: all values are the same
        computedMaxValue = computedMinValue + 1.0;
      } // adjust values by 5 percent in both directions but keep 0


      computedMinValue -= Math.abs(computedMinValue) * 0.05;
      computedMaxValue += Math.abs(computedMaxValue) * 0.05;
    }

    if (scale === ValueRangeScale.log10) {
      let computedMinLog10 = Math.log10(computedMinValue);

      if (size > 0 && computedMinLog10 === Math.floor(computedMinLog10)) {
        // the computed minimum value is an integer multiple of 10 (e.g. 0.1) -> reduce the multiple to ensure all data is visible
        --computedMinLog10;
      }

      computedMinLog10 = Math.floor(computedMinLog10);
      let computedMaxLog10 = Math.ceil(Math.log10(computedMaxValue));

      if (computedMinLog10 === computedMaxLog10) {
        computedMaxLog10 += 1;
      }

      computedMinValue = Math.pow(10.0, computedMinLog10);
      computedMaxValue = Math.pow(10.0, computedMaxLog10);
    }

    if (minValue === undefined) {
      minValue = computedMinValue;
    }

    if (maxValue === undefined) {
      maxValue = computedMaxValue;
    }

    return {
      start: minValue,
      end: maxValue,
      scale: scale
    };
  }

}
